import { useEffect, useRef, useState } from "react";
import { AlertTriangle, FileUp, Loader2, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, statusOf } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { BillForm } from "./BillForm";
import { FileView } from "./FilePreview";

const OK_EXT = ["jpg", "jpeg", "png", "webp", "heic", "heif", "pdf"];
const BAD_FILE = "Upload a JPG, PNG, WEBP, HEIC or PDF under 10 MB";
const NOT_BILL = "This doesn't look like a bill. Try another photo or enter it manually.";
const FAILED = "We couldn't read this bill. Fill in the details below.";

function Picker({ error, onFile, onManual }) {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  return (
    <div>
      <button type="button" onClick={() => input.current.click()} data-testid="upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); e.dataTransfer.files[0] && onFile(e.dataTransfer.files[0]); }}
        className="flex w-full flex-col items-center rounded-2xl border-2 border-dashed px-6 py-12 text-center transition-[border-color,background-color] duration-150"
        style={{ borderColor: drag ? "rgb(var(--glow-money))" : "rgba(var(--glow-money),0.35)", background: drag ? "rgba(var(--glow-money),0.08)" : "rgba(var(--glow-money),0.03)" }}>
        <FileUp className="h-9 w-9" style={{ color: "rgb(var(--glow-money))" }} />
        <span className="mt-4 font-display text-lg font-bold text-q-text">Drop a bill here or tap to choose</span>
        <span className="mt-1 text-sm text-q-muted">Photo or PDF · JPG, PNG, WEBP, HEIC or PDF up to 10 MB</span>
      </button>
      <input ref={input} type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,image/*,application/pdf"
        onChange={(e) => { e.target.files[0] && onFile(e.target.files[0]); e.target.value = ""; }} data-testid="upload-file-input" />
      {error && <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="upload-error">{error}</p>}
      <button type="button" onClick={onManual} className="mt-5 text-sm font-semibold text-q-muted hover:text-q-text" data-testid="upload-manual-btn">Enter it manually instead</button>
    </div>
  );
}

export default function UploadBillDialog({ open, onOpenChange, onSaved }) {
  const { hub } = useHub();
  const [stage, setStage] = useState("pick");
  const [error, setError] = useState("");
  const [local, setLocal] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => () => local?.url && URL.revokeObjectURL(local.url), [local]);

  const upload = async (file) => {
    setError("");
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!file.name.includes(".") || !OK_EXT.includes(ext) || file.size > 10 * 1024 * 1024) return setError(BAD_FILE);
    setLocal({ url: URL.createObjectURL(file), type: file.type || (ext === "pdf" ? "application/pdf" : `image/${ext}`), name: file.name });
    setStage("reading");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/organizations/${hub.id}/bills/scan`, fd, { timeout: 60000 });
      setResult(data);
      setStage(data.status === "not_a_bill" ? "notbill" : "review");
    } catch (e) {
      if (statusOf(e) === 400) {
        setError(errMsg(e));
        setStage("pick");
      } else {
        setResult({ status: "failed", file: null, fields: null });
        setStage("review");
      }
    }
  };

  const manual = () => {
    setLocal(null);
    setResult({ status: "manual", file: null, fields: null });
    setStage("review");
  };

  const reset = () => {
    setStage("pick");
    setResult(null);
    setLocal(null);
  };

  const saved = (bill) => {
    toast.success(`${bill.vendor} saved`);
    onSaved?.(bill);
    onOpenChange(false);
  };

  const withFile = result?.file && local;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-h-[92vh] overflow-y-auto border-q-line bg-q-surface ${stage === "review" && withFile ? "sm:max-w-4xl" : "sm:max-w-lg"}`} data-testid="upload-bill-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold">{stage === "review" ? "Check the details" : "Upload a bill"}</DialogTitle>
          <DialogDescription className="text-q-muted">{stage === "review" ? "Nothing is saved until you press Save." : "We'll read it and fill in the form for you to review."}</DialogDescription>
        </DialogHeader>
        {stage === "pick" && <Picker error={error} onFile={upload} onManual={manual} />}
        {stage === "reading" && (
          <div className="flex flex-col items-center py-12" data-testid="upload-reading">
            <ScanLine className="h-10 w-10 animate-pulse" style={{ color: "rgb(var(--glow-money))" }} />
            <p className="mt-4 font-display text-lg font-bold text-q-text">Reading your bill…</p>
            <div className="mt-5 h-1.5 w-56 overflow-hidden rounded-full bg-q-raised" role="progressbar" aria-label="Reading your bill" data-testid="upload-progress">
              <div className="q-scan-bar h-full w-1/3 rounded-full" style={{ background: "rgb(var(--glow-money))", boxShadow: "0 0 12px rgba(var(--glow-money),0.8)" }} />
            </div>
            <p className="mt-4 text-xs text-q-faint">{local?.name}</p>
          </div>
        )}
        {stage === "notbill" && (
          <div className="py-4" data-testid="upload-not-a-bill">
            {local && <div className="mb-4 max-h-60 overflow-hidden"><FileView url={local.url} contentType={local.type} filename={local.name} testid="upload-preview" /></div>}
            <p className="flex items-start gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-sm font-semibold text-yellow-200" data-testid="upload-not-a-bill-message">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" /> {NOT_BILL}
            </p>
            <div className="mt-5 flex gap-2">
              <button className="q-btn q-btn-primary" onClick={reset} data-testid="upload-try-another-btn">Try another file</button>
              <button className="q-btn q-btn-secondary" onClick={manual} data-testid="upload-enter-manually-btn">Enter it manually</button>
            </div>
          </div>
        )}
        {stage === "review" && (
          <div className={withFile ? "grid gap-6 md:grid-cols-2" : ""} data-testid="upload-review">
            {withFile && <div><FileView url={local.url} contentType={local.type} filename={local.name} testid="upload-preview" /></div>}
            <div>
              {result.status === "failed" && (
                <p className="mb-4 flex items-start gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-sm font-semibold text-yellow-200" data-testid="upload-failed-message">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" /> {FAILED}
                </p>
              )}
              {result.status === "ok" && <p className="mb-4 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: "rgb(var(--glow-money))" }} data-testid="upload-prefilled-note">Filled in from your bill. Check it over.</p>}
              <BillForm hubId={hub.id} initial={result.fields || undefined} fileId={result.file?.id} onSaved={saved} onCancel={() => onOpenChange(false)} />
            </div>
          </div>
        )}
        {stage === "reading" && <Loader2 className="hidden" />}
      </DialogContent>
    </Dialog>
  );
}
